<template>
   <div class="bg-white q-pa-md">
      <h1 class="text-left text-h4">Edit Visual Variable</h1>
      <div class="chip-container">
         <QChip
            id="variable-type-chip"
            color="purple"
            text-color="white"
            >Visual</QChip
         >
         <QChip id="variable-id-chip">Variable Id: {{ id }}</QChip>
      </div>
      <EditVisualVariable
         :variable="localVariable"
         mode="edit"
         @variable-submission="handleSubmit"
      />
   </div>
</template>
<script setup lang="ts">
import EditVisualVariable from "../components/EditVisualVariable.vue"
import { QChip, useQuasar } from "quasar"
import { ref, Ref, onMounted } from "vue"
import { useProjectStore } from "../stores/projectStore"
import { useRoute, useRouter } from "vue-router"
import { toast } from "vue3-toastify"
import { EditableVariable } from "../types/variable"
import { AxiosError } from "axios"
import { collectError, editableVariableFromDBVariable } from "../utils"
import { callWithLoading } from "@utils/callWithLoading"

const projectStore = useProjectStore()
const route = useRoute()
const router = useRouter()
const $q = useQuasar()

const blankVariable: EditableVariable = {
   humanReadableName: "",
   type: "visual",
   version: "0.2",
   key: "",
   defaultValue: "",
   constraints: {
      enumerables: [],
   },
   config: null,
   projectId: 0,
   constraintsVersion: "0.1",
}
const localVariable: Ref<EditableVariable> = ref(blankVariable)
const id = ref(Number(route.params.variableId))

const loadVariableForEdit = async () => {
   try {
      const dbv = await callWithLoading(
         projectStore.getVariable,
         $q,
         Number(route.params.variableId),
      )
      
      // Verify this is a visual variable
      if (dbv.type !== "visual") {
         await router.push({
            name: "Variables",
            params: { projectId: projectStore.projectId },
         })
         toast.error("Variable is not a visual variable")
         return false
      }
      
      localVariable.value = editableVariableFromDBVariable(dbv)
      return true
   } catch (error) {
      collectError(error as Error)
      if (error instanceof AxiosError) {
         const msg = error.response?.data.message
         if (msg) {
            await router.push({
               name: "ProjectHome",
               params: { projectId: projectStore.projectId },
            })
            toast.error(msg)
         }
      } else {
         throw error
      }
      return false
   }
}

// Load variable for editing
onMounted(async () => {
   await loadVariableForEdit()
})

const handleSubmit = async (variable: EditableVariable) => {
   $q.dialog({
      title: `Edit Variable`,
      message: `Your dashboard analytics may not update for 24 hours. Are you sure you want to edit this variable?`,
      cancel: true,
   }).onOk(async () => {
      try {
         $q.loadingBar.start()
         await projectStore.updateVariable(
            Number(route.params.variableId),
            variable,
         )
         $q.loadingBar.increment(50)
         projectStore.variables =
            await projectStore.getVariablesByProjectId()
         $q.loadingBar.stop()
         router.push({ name: "Variables" }).then(() => {
            toast.success("Variable updated.")
         })
      } catch (error) {
         collectError(error as Error)
         if (error instanceof AxiosError) {
            const msg = error.response?.data.message
            if (msg) {
               toast.error(msg)
               return
            }
         }
         toast.error("Failed to update variable.")
         return
      } finally {
         $q.loadingBar.stop()
      }
   })
}
</script>
<style lang="scss" scoped>
h3 {
   margin: 0;
}
.q-input {
   margin-bottom: 10px;
}
.q-select {
   margin-bottom: 10px;
}

#variable-edit-form {
   text-align: left;
   max-width: 500px;
   display: flex;
   flex-direction: column;
}

.chip-container {
   display: flex;
   gap: 8px;
   margin-bottom: 15px;
}

.value-editor-container {
   background-color: #f5f5f5;
   padding: 16px;
   border-radius: 4px;
   margin-bottom: 16px;
   .value-row {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
   }
   .value-list {
      margin-top: 8px;
   }
}
</style>